/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import React from 'react';
import { find } from 'lodash';

import { commonErrors as Errors } from '@zapier/toolbox-redux-framework';

import CenteredSpinner from 'app/developer-v3/components/CenteredSpinner';
import AuthButton from 'app/auth/components/AuthSelect/AuthButton';
import AuthDropdownLabel from 'app/auth/components/AuthSelect/AuthDropdownLabel';
import AuthReconnectButton from 'app/auth/components/AuthSelect/AuthReconnectButton';
import LoadingBlock from 'app/common/components/LoadingBlock';
import Link from 'app/common/components/Link';
import AuthSelect, {
  ADD_NEW_ACCOUNT_VALUE,
  AuthSelectItem,
} from 'app/auth/components/AuthSelect/AuthSelect';
import { getDefaultAuthId } from './utils';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';

import type { AuthType } from 'app/auth/types';
import type { ErrorType } from 'app/common/error-types';
import type { Service } from 'app/service/types';

import type { FlowNode } from '../types';

const Styles = {
  root: css`
    padding: 0 0 20px;

    &:last-of-type {
      padding-bottom: 0;
    }
  `,
  editAccountLink: css`
    float: right;
  `,
};

export type Props = {
  accountHeading?: string;
  auths: Array<AuthType>;
  autoFocus?: boolean;
  clearErrors?: () => void;
  connectAuth: (
    attrs: {},
    options: { onDangerouslyReceiveSuccess: (authId: number) => void }
  ) => void;
  errors?: Array<ErrorType>;
  isAutoSelectionEnabled?: boolean;
  isLoadingAuths: boolean;
  logoUrl: string;
  node: FlowNode;
  onSave?: (nodeAttrs: { authentication_id: number }) => void;
  reconnectAuth: (
    id: number | ID,
    params: {
      service: Service;
      onTrack?: (eventName: string, eventData?: {}) => void;
    },
    onDangerouslyReceiveSuccess: (authId: number) => void
  ) => void;
  service: Service;
  trackEvent?: (eventName: string, eventData?: {}) => void;
  testAuth?: (authId: number) => void;
};

type State = { newAuthId: number };

class Selection extends React.Component<Props, State> {
  state = {
    newAuthId: getDefaultAuthId(this.props),
  };

  static defaultProps = {
    testAuth: () => {},
    onSave: () => {},
    errors: [],
    isAutoSelectionEnabled: true,
    trackEvent: () => {},
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    return state.newAuthId
      ? null
      : {
          newAuthId: getDefaultAuthId(props, { canUseStale: true }),
        };
  }

  componentDidMount() {
    this.autoSelectAuth();
    this.testAccount();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { newAuthId: prevNewAuthId } = prevState;
    const { newAuthId } = this.state;
    const { errors: prevErrors } = prevProps;
    const { auths, errors } = this.props;

    if (!prevNewAuthId && newAuthId) {
      this.autoSelectAuth();
    }

    const selectedAuth = find(auths, { id: this.selectedAuthId() });
    const isError = error => error instanceof Errors.LoadItemsNetworkError;
    const hasAuthErrorPrev = prevErrors.find(isError);
    const hasAuthError = errors.find(isError);

    if (
      !hasAuthErrorPrev &&
      hasAuthError &&
      selectedAuth &&
      !selectedAuth.isStale
    ) {
      this.testAccount();
    }
  }

  autoSelectAuth = () => {
    const { isAutoSelectionEnabled, onSave, node } = this.props;
    const { newAuthId } = this.state;

    if (
      isAutoSelectionEnabled &&
      !node.authentication_id &&
      newAuthId &&
      onSave
    ) {
      onSave({ authentication_id: newAuthId });
    }
  };

  testAccount = (
    authId: number = this.state.newAuthId || this.props.node.authentication_id
  ) => {
    if (authId) {
      this.props.testAuth(authId);
    }
  };

  saveAuth = () => {
    const { newAuthId } = this.state;
    const { node, onSave } = this.props;

    if (newAuthId && newAuthId !== node.authentication_id && onSave) {
      onSave({ authentication_id: newAuthId });
    }

    this.testAccount();
  };

  connectAccount = () => {
    const { connectAuth, service, trackEvent } = this.props;

    connectAuth(
      { service, onTrack: trackEvent },
      {
        onDangerouslyReceiveSuccess: authId =>
          this.setState({ newAuthId: authId }, this.saveAuth),
      }
    );
  };

  reconnectAuth = (id: number | ID) => {
    const { clearErrors, reconnectAuth, service, trackEvent } = this.props;

    const onDangerouslyReceiveSuccess = authId =>
      this.setState({ newAuthId: authId }, () => {
        this.saveAuth();
        // If there was an auth error that was shown, we want to clear it or
        // else the Simple Zap 'Turn On' button will be disabled. The check
        // below is to make sure this only happens for Simple Zap Setup module,
        // not the Simpliefied Editor.
        if (clearErrors) {
          clearErrors();
        }
      });

    reconnectAuth(
      id,
      { service, onTrack: trackEvent },
      onDangerouslyReceiveSuccess
    );
  };

  onSelectAccount = (newAuthId: number) => {
    this.setState({ newAuthId }, this.saveAuth);
  };

  selectedAuthId() {
    return this.state.newAuthId || this.props.node.authentication_id;
  }

  onChange = (choice: AuthSelectItem) => {
    const { value, isStale } = choice;

    if (value === ADD_NEW_ACCOUNT_VALUE) {
      this.connectAccount();
    } else if (isStale) {
      this.reconnectAuth(value);
    } else {
      this.onSelectAccount(value);
    }
  };

  render() {
    const {
      auths,
      autoFocus,
      isLoadingAuths,
      logoUrl,
      node,
      service,
    } = this.props;

    if (!node) {
      return <LoadingBlock />;
    }

    const { name, id: serviceId } = service;
    const selectedAuth = find(auths, { id: this.selectedAuthId() });
    const formattedSelectedAuth = selectedAuth
      ? {
          value: selectedAuth.id,
          label: selectedAuth.title,
          isStale: selectedAuth.isStale,
        }
      : undefined;

    return (
      <section css={Styles.root}>
        {isLoadingAuths ? (
          <CenteredSpinner aria-label="Loading Accounts" />
        ) : auths.length > 0 ? (
          <AuthSelect
            api={serviceId}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- pass-through of an opt-in prop; the focus decision is made by the caller (AuthAccount)
            autoFocus={autoFocus}
            items={auths.map(auth => ({
              addedDate: auth.addedDate,
              api: auth.api,
              authType: auth.type,
              connectionLabel: auth.connectionLabel,
              // Selecting stale auth will open reconnect modal so we don't want it disabled
              isDisabled: !auth.isStale && selectedAuth?.id === auth.id,
              isStale: auth.isStale,
              label: auth.title,
              numZaps: auth.numZaps,
              value: auth.id,
            }))}
            labelElement={
              <React.Fragment>
                <AuthDropdownLabel
                  accountHeading={this.props.accountHeading}
                  name={name}
                />
                <span css={Styles.editAccountLink}>
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/app/connections`}
                    target="_blank"
                  >
                    Edit Accounts
                  </Link>
                </span>
              </React.Fragment>
            }
            labelId="auth-label"
            menuAriaLabel={name ? `List of ${name} accounts` : ''}
            onChange={this.onChange}
            selectedItem={formattedSelectedAuth}
          />
        ) : (
          <AuthButton
            connectAccount={this.connectAccount}
            logoUrl={logoUrl}
            serviceName={name}
          />
        )}
        {selectedAuth?.isStale && (
          <AuthReconnectButton
            onReconnectAuth={this.reconnectAuth}
            selectedAuthId={selectedAuth.id}
          />
        )}
      </section>
    );
  }
}

export default Selection;
